import { Injectable } from '@angular/core';
import { ModalController, ModalOptions, Modal } from 'ionic-angular';
import { OnPremiseProvider } from '../on-premise';
import { PopupTrackingObject, ModalProperties, PriorityLevel, NotificationResult } from './base';
import { TranslateService } from '@ngx-translate/core';

/**
 * Provides a simple modal mechanism that does not depend on the settingsProvider/apiProvider
 * There is no doNotShow functionality here (which requires user authentication)
 */
@Injectable()
export class ModalProvider {
    private modals: PopupTrackingObject[] = [];
    private closingModals = false;

    constructor(
        private modalCtrl: ModalController,
        private onPremiseProvider: OnPremiseProvider,
        private translate: TranslateService
    ) {}

    public async presentAlert(title: string, message: string, primaryText = 'OK'): Promise<void> {
        await this.createNotification<NotificationResult>({
            priority: PriorityLevel.Notification,
            title,
            message,
            primaryText,
            secondaryButtons: []
        });
    }

    public async presentError(
        title: string,
        message: string,
        materialIconName = 'error',
        primaryText = 'OK',
        customCSS?: string
    ): Promise<void> {
        await this.createNotification<NotificationResult>({
            priority: PriorityLevel.Error,
            title,
            message,
            primaryText,
            materialIconName,
            secondaryButtons: [],
            customCSS
        });
    }

    public async presentConfirmationPrompt(
        title: string,
        message: string,
        primaryText = 'OK',
        primaryButtonCSS?: string
    ): Promise<boolean> {
        const result = await this.createNotification<NotificationResult>({
            priority: PriorityLevel.Warning,
            title,
            message,
            primaryText,
            primaryButtonCSS,
            secondaryButtons: [{ text: this.translate.instant('Cancel'), callback: () => {} }]
        });

        return result !== undefined && result !== null && result.primaryClicked == true;
    }

    public async presentModal(name: string, data?: any, opts?: ModalOptions): Promise<any> {
        return await new Promise(resolve => {
            const modal = this.createModal(name, data, opts);

            modal.onDidDismiss(async data => {
                resolve(data);
            });

            modal.present();
        });
    }

    public async createNotification<ReturnType>(properties: ModalProperties): Promise<ReturnType> {
        return await new Promise<any>(async resolve => {
            const modal = this.createModal(
                'NotificationModal',
                { ...properties },
                { cssClass: `notification-modal ${properties.customCSS}` }
            );

            modal.onDidDismiss(data => resolve(data));
            modal.present();
        });
    }

    public createModal(component: any, data?: any, opts?: ModalOptions): Modal {
        const isOnPremise = this.onPremiseProvider.isOnPremiseElectronApp();

        if (isOnPremise) {
            // If we're running on-premise Brain Builder, find a way to monkey-patch this class in.
            if (!opts) {
                opts = {
                    cssClass: 'on-premise-mode'
                };
            } else if (!opts.cssClass) {
                opts.cssClass = 'on-premise-mode';
            } else {
                opts.cssClass = opts.cssClass.concat(' on-premise-mode');
            }
        }

        const modal = this.modalCtrl.create(component, data, opts);

        // In order to bake in on-dismiss logic without clobbering the 'onDidDismiss' handler,
        // we hook onWillDismiss (which fires just before the dismissal animation).
        modal.onWillDismiss(() => {
            const modalIndexToRemove = this.modals.findIndex(modal => {
                return component === modal.componentName;
            });
            if (modalIndexToRemove >= 0) {
                this.modals.splice(modalIndexToRemove, 1);
            }
        });

        const modalAlreadyExists = this.modals.find(modal => {
            return component === modal.componentName;
        });

        if (modalAlreadyExists) {
            // Short-circuit the modal and prevent it from showing.
            modal.present = async () => {};
            return modal;
        }

        // If we haven't already created this modal, start tracking it.
        this.modals.push({ componentName: component, component: modal });

        return modal;
    }

    public modalIsOpen(modalName: string): boolean {
        return this.modals.some((modal: PopupTrackingObject) => modal.componentName === modalName);
    }

    public async closeAll(): Promise<void> {
        if (!this.closingModals) {
            this.closingModals = true;

            try {
                await Promise.all(this.modals.map(async (popover: PopupTrackingObject) => popover.component.dismiss()));
            } catch (err) {
                throw err;
            } finally {
                this.modals = [];
                this.closingModals = false;
            }
        }
    }

    // Get the entire scrollable height of the page to use as a custom height for an overlay.
    // This is currently only used for the menu overlays in the project and dataset listings.
    public computeCustomOverlayHeight(): string {
        const elementForPageHeight = document.querySelector('.scroll-content');

        if (elementForPageHeight) {
            return `${elementForPageHeight.scrollHeight}px`;
        } else {
            // The scroll height should be defined, but we need a fallback in case it somehow isn't.
            return `100%`;
        }
    }
}



// WEBPACK FOOTER //
// ./src/providers/popup/modal.ts